import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import OmsLogo from "@/components/OmsLogo";

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("admin@zaroorijini.com");
  const [password, setPassword] = useState("Admin@12345");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      const defaultTo = user.role === "customer_admin" ? "/portal" : "/dashboard";
      const to = loc.state?.from?.pathname || defaultTo;
      nav(to, { replace: true });
    }
  }, [user, nav, loc.state]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      const role = res?.role;
      const defaultTo = role === "customer_admin" ? "/portal" : "/dashboard";
      toast.success("Signed in");
      nav(loc.state?.from?.pathname || defaultTo, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-border bg-card">
        <div className="flex items-center gap-3">
          <div style={{ background: "#fff", padding: "10px 14px", borderRadius: 8 }}>
            <img
              src="https://customer-assets-cm19k8pv.emergentagent.net/job_scalable-fashion-oms/artifacts/es403apk_zaroori%20jini%20oms.png"
              alt="Zaroori Jini OMS"
              style={{ height: 40, width: "auto", objectFit: "contain", display: "block" }}
              data-testid="login-oms-logo"
            />
          </div>
        </div>

        <div>
          <div className="label-mono mb-3">Command center</div>
          <h1 className="text-4xl md:text-5xl leading-[1.05] tracking-tight mb-4">
            One deployment.
            <br /><span className="text-primary">Thousands of brands.</span>
          </h1>
          <p className="text-sm text-muted-foreground max-w-md">
            Multi-tenant, multi-warehouse, multi-marketplace order management for
            fashion, beauty and lifestyle brands. AI-native. Event-driven.
          </p>

          <div className="grid grid-cols-2 gap-3 mt-8 max-w-md">
            {[
              ["ISOLATION", "Row-level tenant scoping"],
              ["AI INSIGHTS", "Claude Sonnet 4.5"],
              ["API-FIRST", "Every action is an endpoint"],
              ["ZERO-BREAKING", "Extend, never rewrite"],
            ].map(([k, v]) => (
              <div key={k} className="surface p-3">
                <div className="label-mono mb-1">{k}</div>
                <div className="text-xs">{v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="label-mono">© Zaroori Jini · Bharat · 2026</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="surface w-full max-w-sm p-8" data-testid="login-form">
          <div className="label-mono mb-2">Access</div>
          <h2 className="text-2xl mb-6">Sign in to your workspace</h2>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="label-mono">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="mt-1.5 font-mono text-sm"
                style={{ borderRadius: 2 }}
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="label-mono">Password</Label>
                <a href="/forgot-password" className="text-[10px] font-mono uppercase tracking-widest text-primary hover:underline" data-testid="login-forgot-link">Forgot?</a>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="mt-1.5 font-mono text-sm"
                style={{ borderRadius: 2 }}
              />
            </div>

            {error && (
              <div className="text-xs text-destructive font-mono" data-testid="login-error">{error}</div>
            )}

            <Button
              type="submit"
              className="w-full font-mono uppercase tracking-widest text-xs"
              style={{ borderRadius: 2 }}
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : "Sign in →"}
            </Button>

            <div className="border-t border-border pt-4 space-y-1">
              <div className="label-mono">Demo credentials</div>
              <div className="text-xs font-mono">admin@zaroorijini.com · Admin@12345</div>
              <div className="text-xs font-mono text-muted-foreground">agency@zaroorijini.com · Agency@12345</div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
